import { useContext, useEffect } from 'react';

import { Button, ChevronDownIcon } from '@selina-finance/ui';
import { SelectBoxItem } from '@selina-finance/ui/build/components/SelectBox';
import clsx from 'clsx';
import { Form } from 'formik';
import { mapKeys, omit } from 'lodash';
import mixpanel from 'mixpanel-browser';
import { useTranslation } from 'react-i18next';

import { ApplicationContext } from '@App/contexts/Application';
import {
    DrawdownFlexibility,
    accessArray,
} from '@FormModels/eligibility/loanPreferences';
import { InfoNote } from '@Views/eligibility/loan-preferences/components/InfoNote';
import { QuestionController } from '@Views/eligibility/loan-preferences/components/QuestionController';

import {
    QuestionPageFormKeys,
    drawdownFlexibilityTermItems,
    fullEarlyRepaymentMethodItems,
    mixpanelEventNames,
} from './constants';
import {
    getIsMoveToRates,
    getIsRetireBeforeRedeem,
    mixpanelValuesConverter,
} from './helpers';

export interface QuestionPageFormProps {
    isRatesVisible: boolean;
    answers: any;
    formikProps: any;
    yearItems: SelectBoxItem[];
    getError(name: string): string;
    setIsRatesVisible(arg: boolean): void;
}

export const QuestionPageForm = ({
    isRatesVisible,
    answers,
    formikProps,
    yearItems,
    getError,
    setIsRatesVisible,
}: QuestionPageFormProps) => {
    const { applicants, incomings } = useContext(ApplicationContext);

    const { t } = useTranslation('loanPreferences');

    const isRetireBeforeRedeem = getIsRetireBeforeRedeem(
        applicants[0].dateOfBirth,
        formikProps.values.fullEarlyRepaymentYear,
        incomings[0].estimatedRetirementAge,
    );

    const isMoveToRates = getIsMoveToRates(formikProps.values);

    useEffect(() => {
        if (formikProps.values !== answers) {
            setIsRatesVisible(false);
        }
    }, [answers, formikProps.values, setIsRatesVisible]);

    const isButtonEnabled = isRetireBeforeRedeem || isMoveToRates;

    const questionsConfig = [
        {
            key: QuestionPageFormKeys.fullEarlyRepayment,
            props: {
                label: t('fullEarlyRepayment.label'),
                infoNote: <InfoNote note={t('fullEarlyRepayment.note')} />,
            },
            isVisible: () => true,
        },
        {
            key: QuestionPageFormKeys.fullEarlyRepaymentYear,
            props: {
                isRadio: false,
                label: t('fullEarlyRepaymentYear.label'),
                items: yearItems,
                suffix: <ChevronDownIcon />,
                placeholder: t('fullEarlyRepaymentYear.placeholder'),
                onBlur: () => {
                    return formikProps.setTouched({
                        ...formikProps.touched,
                    });
                },
            },
            isVisible: (values: any) => values.fullEarlyRepayment === 'true',
        },
        {
            key: QuestionPageFormKeys.fullEarlyRepaymentMethod,
            props: {
                isRadio: false,
                label: t('fullEarlyRepaymentMethod.label'),
                items: fullEarlyRepaymentMethodItems,
                suffix: <ChevronDownIcon />,
                placeholder: t('fullEarlyRepaymentMethod.placeholder'),
                onBlur: () => {
                    return formikProps.setTouched({
                        ...formikProps.touched,
                    });
                },
            },
            isVisible: (values: any) => values.fullEarlyRepaymentYear,
        },
        {
            key: QuestionPageFormKeys.drawdownFlexibility,
            props: {
                label: t('drawdownFlexibility.label'),
                infoNote: (
                    <InfoNote
                        note={t('drawdownFlexibility.note')}
                        expandedNote={t('drawdownFlexibility.expandedNote')}
                    />
                ),
                customRadioButtonOptions: accessArray,
                className: 'flex-col gap-1',
            },
            isVisible: (values: any) =>
                (values.fullEarlyRepaymentMethod &&
                    !isRetireBeforeRedeem &&
                    values.fullEarlyRepayment === 'true') ||
                values.fullEarlyRepayment === 'false',
        },
        {
            key: QuestionPageFormKeys.drawdownFlexibilityTerm,
            props: {
                isRadio: false,
                label: t('drawdownFlexibilityTerm.label'),
                items: drawdownFlexibilityTermItems(),
                suffix: <ChevronDownIcon />,
                placeholder: t('drawdownFlexibilityTerm.placeholder'),
                onBlur: () => {
                    return formikProps.setTouched({
                        ...formikProps.touched,
                    });
                },
            },
            isVisible: (values: any) =>
                values.drawdownFlexibility === DrawdownFlexibility.AS_NEEDED,
        },
        {
            key: QuestionPageFormKeys.neededAmountAndTimeKnown,
            props: {
                label: t('neededAmountAndTimeKnown.label'),
            },
            isVisible: (values: any) => values.drawdownFlexibilityTerm,
        },
        {
            key: QuestionPageFormKeys.monthlyOverpaymentsPlanned,
            props: {
                label: t('monthlyOverpaymentsPlanned.label'),
                infoNote: (
                    <InfoNote note={t('monthlyOverpaymentsPlanned.note')} />
                ),
            },
            isVisible: (values: any) =>
                values.drawdownFlexibility ===
                    DrawdownFlexibility.ALL_AT_ONCE ||
                (values.drawdownFlexibility === DrawdownFlexibility.AS_NEEDED &&
                    values.neededAmountAndTimeKnown),
        },
        {
            key: QuestionPageFormKeys.yearlyOverpaymentAmountKnown,
            props: {
                label: t('yearlyOverpaymentAmountKnown.label'),
            },
            isVisible: (values: any) =>
                (values.drawdownFlexibility === DrawdownFlexibility.AS_NEEDED &&
                    values.monthlyOverpaymentsPlanned === 'true') ||
                (values.drawdownFlexibility ===
                    DrawdownFlexibility.ALL_AT_ONCE &&
                    values.monthlyOverpaymentsPlanned === 'true'),
        },
        {
            key: QuestionPageFormKeys.moreThan10PercentNoFeesOverpaymentPlanned,
            props: {
                label: t('moreThan10PercentNoFeesOverpaymentPlanned.label'),
                infoNote: (
                    <InfoNote
                        note={t(
                            'moreThan10PercentNoFeesOverpaymentPlanned.note',
                        )}
                        expandedNote={t(
                            'moreThan10PercentNoFeesOverpaymentPlanned.expandedNote',
                        )}
                    />
                ),
            },
            isVisible: (values: any) => values.yearlyOverpaymentAmountKnown,
        },
    ];

    const onChangeForm = (e: any, fieldName: string, formikProps: any) => {
        const resetFields = (fieldsToOmit: string[] = []) => {
            mapKeys(omit(formikProps.values, fieldsToOmit), (_, key) =>
                formikProps.setFieldValue(key, null),
            );
        };

        const fieldResetMap: Record<string, string[]> = {
            fullEarlyRepayment: [],
            fullEarlyRepaymentYear: [QuestionPageFormKeys.fullEarlyRepayment],
            fullEarlyRepaymentMethod: [
                QuestionPageFormKeys.fullEarlyRepayment,
                QuestionPageFormKeys.fullEarlyRepaymentYear,
            ],
            drawdownFlexibility: [
                QuestionPageFormKeys.fullEarlyRepayment,
                QuestionPageFormKeys.fullEarlyRepaymentYear,
                QuestionPageFormKeys.fullEarlyRepaymentMethod,
            ],
            drawdownFlexibilityTerm: [
                QuestionPageFormKeys.fullEarlyRepayment,
                QuestionPageFormKeys.fullEarlyRepaymentYear,
                QuestionPageFormKeys.fullEarlyRepaymentMethod,
                QuestionPageFormKeys.drawdownFlexibility,
            ],
            monthlyOverpaymentsPlanned: [
                QuestionPageFormKeys.fullEarlyRepayment,
                QuestionPageFormKeys.fullEarlyRepaymentYear,
                QuestionPageFormKeys.fullEarlyRepaymentMethod,
                QuestionPageFormKeys.drawdownFlexibility,
                QuestionPageFormKeys.drawdownFlexibilityTerm,
                QuestionPageFormKeys.neededAmountAndTimeKnown,
            ],
            yearlyOverpaymentAmountKnown: [
                QuestionPageFormKeys.fullEarlyRepayment,
                QuestionPageFormKeys.fullEarlyRepaymentYear,
                QuestionPageFormKeys.fullEarlyRepaymentMethod,
                QuestionPageFormKeys.drawdownFlexibility,
                QuestionPageFormKeys.drawdownFlexibilityTerm,
                QuestionPageFormKeys.neededAmountAndTimeKnown,
                QuestionPageFormKeys.monthlyOverpaymentsPlanned,
            ],
            neededAmountAndTimeKnown: [
                QuestionPageFormKeys.fullEarlyRepayment,
                QuestionPageFormKeys.fullEarlyRepaymentYear,
                QuestionPageFormKeys.fullEarlyRepaymentMethod,
                QuestionPageFormKeys.drawdownFlexibility,
                QuestionPageFormKeys.drawdownFlexibilityTerm,
                QuestionPageFormKeys.neededAmountAndTimeKnown,
            ],
        };

        if (fieldResetMap[fieldName]) {
            resetFields(fieldResetMap[fieldName]);
        }

        formikProps.setFieldValue(fieldName, e.currentTarget.value);

        mixpanel.track(mixpanelEventNames[fieldName], {
            'Selected option': mixpanelValuesConverter(e.currentTarget.value),
        });
    };

    return (
        <Form>
            {questionsConfig.map(({ key, props, isVisible }, index) => {
                if (!isVisible(formikProps.values)) return null;

                return (
                    <div key={key} className={clsx(index !== 0 && 'mt-7')}>
                        <QuestionController
                            isRadio
                            name={key}
                            value={formikProps.values[key]?.toString() || ''}
                            getError={getError}
                            onChange={(e: any) => {
                                onChangeForm(e, key, formikProps);
                            }}
                            {...props}
                        />
                    </div>
                );
            })}

            {!isRatesVisible && (
                <div className='flex gap-5 justify-end mt-12'>
                    <Button
                        color='secondary'
                        type='submit'
                        size='large'
                        className='w-full'
                        disabled={!isButtonEnabled}
                    >
                        Continue
                    </Button>
                </div>
            )}
        </Form>
    );
};
